import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NumericInput,
    ToggleGroup,
    Breadcrumb,
    TextInput,
    Accordion,
    Separator,
    SelectBox,
    Progress,
    Avatar,
    Button,
    Alert,
    Badge,
    Card,
    Chip,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ShoppingCart,
    RefreshCw,
    ThumbsUp,
    Search,
    Check,
    User
} from "lucide-react-native";
import {
    Ellipse,
    Circle,
    Path,
    Rect,
    Svg,
    G
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const CartIcon = createKitIcon(ShoppingCart);

const SearchIcon = createKitIcon(Search);

const UserIcon = createKitIcon(User);

const RefreshIcon = createKitIcon(RefreshCw);

const LikeIcon = createKitIcon(ThumbsUp);

const CheckIcon = createKitIcon(Check);

const NAV = [
    "coffees",
    "gear",
    "plans",
    "gift"
];

const STARS = [
    1,
    2,
    3,
    4,
    5
];

const SCENES = [
    "bag",
    "beans",
    "cup",
    "dripper"
];

const SCENE_COLORS: Record<string, string> = {
    dripper: "3",
    beans: "4",
    bag: "1",
    cup: "2"
};

const SIZES = [
    {
        price: 285,
        id: "250"
    },
    {
        price: 540,
        id: "500"
    },
    {
        price: 980,
        id: "1000"
    }
];

const GRINDS = [
    "filter",
    "espresso",
    "moka",
    "press",
    "whole"
];

const NOTES = [
    "bergamot",
    "peach",
    "tea",
    "lemon"
];

const BADGES = [
    "season",
    "roast",
    "origin"
];

const FACTS = [
    "origin",
    "altitude",
    "variety",
    "process",
    "roast",
    "body"
];

const SECTIONS = [
    "info",
    "brew",
    "shipping"
];

const HISTOGRAM = [
    {
        count: 358,
        stars: 5
    },
    {
        count: 41,
        stars: 4
    },
    {
        count: 9,
        stars: 3
    },
    {
        count: 3,
        stars: 2
    },
    {
        count: 1,
        stars: 1
    }
];

const REVIEWS = [
    {
        id: "zeynep",
        helpful: 24,
        stars: 5
    },
    {
        helpful: 11,
        id: "can",
        stars: 4
    }
];

const BREW = [
    "dose",
    "water",
    "time"
];

const FOOTER = [
    "delivery",
    "privacy",
    "contact"
];

const REVIEW_TOTAL = 412;

const OLD_PRICE = 335;

const STAR_PATH = "M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26Z";

const BAG_BODY = "M38 36h84l9 126a7 7 0 0 1-7 7H36a7 7 0 0 1-7-7z";

const BAG_SHOULDER = "M38 36h84l1.4 20H36.6z";

const BAG_CAP = "M44 22c0-4 4-6 8-6h56c4 0 8 2 8 6v14H44z";

const BAG_SHINE = "M120 60l8 100";

const BAG_LEAF = "M74 106c2-8 8-12 14-12-1 7-5 12-14 12z";

const MARK_BAG = "M8 11h12l-1.4 8.2a2 2 0 0 1-2 1.8h-5.2a2 2 0 0 1-2-1.8z";

const MARK_HANDLE = "M10.5 11a3.5 3.5 0 0 1 7 0";

const BEANS = [
    {
        transform: "rotate(-30 34 38)",
        cx: 34,
        cy: 38,
        rx: 17,
        ry: 11
    },
    {
        transform: "rotate(20 64 34)",
        cx: 64,
        cy: 34,
        rx: 17,
        ry: 11
    },
    {
        transform: "rotate(-8 50 62)",
        cx: 50,
        cy: 62,
        rx: 17,
        ry: 11
    },
    {
        transform: "rotate(40 24 68)",
        cx: 24,
        cy: 68,
        rx: 14,
        ry: 9
    },
    {
        transform: "rotate(-35 76 66)",
        cx: 76,
        cy: 66,
        rx: 14,
        ry: 9
    }
];

const BEAN_CREASES = [
    "M22 44c8-8 18-12 24-8",
    "M52 40c8-6 18-8 24-2",
    "M38 66c8-4 18-4 24 0"
];

const CUP_BODY = "M24 42h48v18a22 22 0 0 1-22 22h-4a22 22 0 0 1-22-22z";

const CUP_HANDLE = "M72 48h6a8 8 0 0 1 0 16h-8";

const CUP_STEAM = "M38 30c-3-4 3-6 0-10M50 30c-3-4 3-6 0-10M62 30c-3-4 3-6 0-10";

const DRIPPER_CONE = "M26 18h48l-14 24H40z";

const DRIPPER_BODY = "M32 52h36l-3 30a5 5 0 0 1-5 4H40a5 5 0 0 1-5-4z";

const DRIPPER_FILL = "M34 66h32l-1.5 16a5 5 0 0 1-5 4H40a5 5 0 0 1-5-4z";

const stylesheet = StyleSheet.create({
    strike: {
        textDecorationLine: "line-through"
    },
    spread: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    stage: {
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        aspectRatio: 1.05,
        width: "100%"
    },
    thumb: {
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        aspectRatio: 1.2,
        flex: 1
    },
    columns: {
        alignItems: "flex-start"
    },
    priceRow: {
        alignItems: "baseline",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    wrapper: {
        alignSelf: "center",
        width: "100%"
    },
    thumbs: {
        flexDirection: "row"
    },
    announcement: {
        position: "absolute"
    },
    countCell: {
        textAlign: "right",
        width: "10%"
    },
    fact: {
        flexBasis: "45%",
        flexGrow: 1
    },
    figure: {
        height: "100%",
        width: "100%"
    },
    grow: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    full: {
        width: "100%"
    },
    search: {
        minWidth: 0,
        flex: 1
    },
    bar: {
        flex: 1
    }
});

type SceneProps = {
    paper: string;
    fill: string;
    kind: string;
    ink: string;
};

const BagScene = ({
    paper,
    fill,
    ink
}: Omit<SceneProps, "kind">) => <G>
    <Ellipse
        opacity={0.14}
        fill={ink}
        cy={170}
        cx={80}
        rx={50}
        ry={5}
    />
    <Path
        d={BAG_BODY}
        fill={fill}
    />
    <Path
        d={BAG_SHOULDER}
        opacity={0.16}
        fill={ink}
    />
    <Path
        fill={fill}
        d={BAG_CAP}
    />
    <Rect
        opacity={0.22}
        height={6}
        width={72}
        fill={ink}
        rx={2}
        x={44}
        y={30}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={6}
        stroke={paper}
        opacity={0.14}
        d={BAG_SHINE}
        fill="none"
    />
    <Rect
        fill={paper}
        height={70}
        width={68}
        rx={6}
        x={46}
        y={78}
    />
    <Circle
        strokeWidth={3}
        stroke={fill}
        fill="none"
        cy={102}
        cx={80}
        r={12}
    />
    <Path
        d={BAG_LEAF}
        fill={fill}
    />
    <Rect
        opacity={0.8}
        height={4}
        width={44}
        fill={ink}
        y={124}
        rx={2}
        x={58}
    />
    <Rect
        opacity={0.35}
        height={3}
        width={28}
        fill={ink}
        rx={1.5}
        y={132}
        x={66}
    />
</G>;

const BeansScene = ({
    paper,
    fill
}: Omit<SceneProps, "kind">) => <G>
    {
        BEANS.map((bean) => <Ellipse
            transform={bean.transform}
            key={bean.transform}
            cx={bean.cx}
            cy={bean.cy}
            rx={bean.rx}
            ry={bean.ry}
            fill={fill}
        />)
    }
    {
        BEAN_CREASES.map((crease) => <Path
            strokeLinecap="round"
            strokeWidth={2}
            stroke={paper}
            key={crease}
            fill="none"
            d={crease}
        />)
    }
</G>;

const CupScene = ({
    paper,
    fill,
    ink
}: Omit<SceneProps, "kind">) => <G>
    <Ellipse
        opacity={0.14}
        fill={ink}
        cx={50}
        cy={84}
        rx={34}
        ry={5}
    />
    <Path
        d={CUP_BODY}
        fill={paper}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={5}
        d={CUP_HANDLE}
        stroke={paper}
        fill="none"
    />
    <Ellipse
        fill={fill}
        cx={48}
        cy={42}
        rx={24}
        ry={4}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={2.5}
        d={CUP_STEAM}
        stroke={fill}
        fill="none"
    />
</G>;

const DripperScene = ({
    paper,
    fill
}: Omit<SceneProps, "kind">) => <G>
    <Path
        d={DRIPPER_CONE}
        fill={paper}
    />
    <Rect
        fill={fill}
        height={6}
        width={32}
        rx={2}
        x={34}
        y={42}
    />
    <Path
        d={DRIPPER_BODY}
        opacity={0.85}
        fill={fill}
    />
    <Path
        d={DRIPPER_FILL}
        fill={fill}
    />
    <Path
        strokeLinecap="round"
        strokeWidth={2}
        stroke={paper}
        d="M50 48v8"
    />
</G>;

const Scene = ({
    paper,
    fill,
    kind,
    ink
}: SceneProps) => {
    const Drawing = SCENE_DRAWINGS[kind] ?? BagScene;

    return <Svg
        viewBox={kind === "bag" ? "0 0 160 180" : "0 0 100 100"}
        preserveAspectRatio="xMidYMid meet"
        style={stylesheet.figure}
    >
        <Drawing
            paper={paper}
            fill={fill}
            ink={ink}
        />
    </Svg>;
};

const SCENE_DRAWINGS: Record<string, (props: Omit<SceneProps, "kind">) => ReactNode> = {
    dripper: DripperScene,
    beans: BeansScene,
    bag: BagScene,
    cup: CupScene
};

type StarsProps = {
    empty: string;
    value: number;
    size: number;
    fill: string;
};

const Stars = ({
    empty,
    value,
    size,
    fill
}: StarsProps) => <Svg
    viewBox="0 0 120 24"
    width={size * 5}
    height={size}
>
    {
        STARS.map((star) => <Path
            fill={star <= Math.round(value) ? fill : empty}
            transform={`translate(${(star - 1) * 24} 0)`}
            d={STAR_PATH}
            key={star}
        />)
    }
</Svg>;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const Product01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        scene,
        setScene
    ] = useState("bag");

    const [
        size,
        setSize
    ] = useState("250");

    const [
        quantity,
        setQuantity
    ] = useState("2");

    const [
        cartCount,
        setCartCount
    ] = useState(3);

    const [
        liked,
        setLiked
    ] = useState<Array<string>>([]);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isStacked = width < shapeConfigs.wideBreakpoint;

    const categorical = colors.chart.categorical;

    const categoricalArea = colors.chart.categoricalArea;

    const ink = colors.content.container.inverse;

    const paper = colors.content.container.default;

    const money = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const decimal = new Intl.NumberFormat(activeLocale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        month: "short",
        day: "numeric"
    });

    const unitPrice = SIZES.find((item) => item.id === size)?.price ?? SIZES[0]?.price ?? 0;

    const count = Math.max(1, Number(quantity) || 1);

    const grindOptions = GRINDS.map((key) => ({
        name: t(`blocks.product01.grind.${key}`),
        id: key
    }));

    const handlePress = () => undefined;

    const getColor = (key: string) => categorical[key as keyof typeof categorical];

    const getArea = (key: string) => categoricalArea[key as keyof typeof categoricalArea];

    const handleScene = (key: string) => () => setScene(key);

    const handleSize = (value: string | Array<string>) => setSize(typeof value === "string" && value !== "" ? value : size);

    const handleAdd = () => setCartCount((current) => current + count);

    const handleLike = (id: string) => () => setLiked((current) => current.includes(id) ? current.filter((item) => item !== id) : [
        ...current,
        id
    ]);

    const renderNav = (key: string, index: number) => <Button
        variant={index === 0 ? "tonal" : "ghost"}
        title={t(`blocks.product01.nav.${key}`)}
        onPress={handlePress}
        type="neutral"
        size="small"
        key={key}
    />;

    const renderBadge = (key: string) => <Badge
        variant={key === "season" ? "tonal" : "outline"}
        type={key === "season" ? "primary" : "neutral"}
        title={t(`blocks.product01.badge.${key}`)}
        key={key}
    />;

    const renderNote = (key: string) => <Chip
        title={t(`blocks.product01.note.${key}`)}
        variant="outline"
        type="neutral"
        key={key}
    />;

    const renderSize = (item: typeof SIZES[number]) => <ToggleGroup.Item
        title={t(`blocks.product01.size.${item.id}`)}
        value={item.id}
        key={item.id}
    />;

    const renderThumb = (key: string, index: number) => {
        const isSelected = key === scene;

        return <Card
            style={[
                stylesheet.thumb,
                {
                    backgroundColor: getArea(SCENE_COLORS[key] ?? "1")
                },
                isSelected ? {
                    borderColor: colors.content.border.emphasized,
                    borderWidth: borders.selection
                } : undefined
            ]}
            accessibilityLabel={t(`blocks.product01.thumb.${key}`, [
                index + 1
            ])}
            onPress={handleScene(key)}
            isPressable={true}
            variant="outline"
            key={key}
        >
            <View
                style={[
                    stylesheet.full,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
            >
                <Scene
                    fill={getColor(SCENE_COLORS[key] ?? "1")}
                    paper={paper}
                    kind={key}
                    ink={ink}
                />
            </View>
        </Card>;
    };

    const renderFact = (key: string) => <View
        style={[
            stylesheet.fact,
            {
                minWidth: shapeConfigs.sideListWidth / 2,
                gap: spaces.spacingXxs
            }
        ]}
        key={key}
    >
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.product01.fact.${key}`)}
        </Text>
        <Text
            variant="bodyMediumSize"
            color="high"
        >
            {t(`blocks.product01.fact.${key}.value`)}
        </Text>
    </View>;

    const renderBrew = (key: string) => <View
        style={stylesheet.grow}
        key={key}
    >
        <Text
            variant="titleLargeSize"
            color="high"
        >
            {t(`blocks.product01.brew.${key}`)}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.product01.brew.${key}.unit`)}
        </Text>
    </View>;

    const renderSection = (key: string) => <Accordion.Item
        value={key}
        key={key}
    >
        <Accordion.Trigger
            title={t(`blocks.product01.section.${key}`)}
        />
        <Accordion.Content>
            {
                key === "info" ?
                    <View
                        style={[
                            stylesheet.wrap,
                            {
                                columnGap: spaces.spacingLg,
                                rowGap: spaces.spacingMd
                            }
                        ]}
                    >
                        {FACTS.map(renderFact)}
                    </View>
                :
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t(`blocks.product01.section.${key}.text`)}
                    </Text>
            }
        </Accordion.Content>
    </Accordion.Item>;

    const renderBar = (row: typeof HISTOGRAM[number]) => <View
        style={[
            stylesheet.row,
            {
                gap: spaces.spacingSm
            }
        ]}
        key={row.stars}
    >
        <Text
            style={{
                minWidth: spaces.spacingXl
            }}
            variant="bodyMediumSize"
            color="mid"
        >
            {`${row.stars} ★`}
        </Text>
        <Progress
            accessibilityLabel={t("blocks.product01.reviews.bar", [
                row.stars,
                row.count
            ])}
            style={stylesheet.bar}
            max={REVIEW_TOTAL}
            value={row.count}
            type="warning"
        >
            <Progress.Track/>
        </Progress>
        <Text
            style={[
                stylesheet.countCell,
                {
                    minWidth: spaces.spacingXl
                }
            ]}
            variant="bodySmallSize"
            color="mid"
        >
            {row.count}
        </Text>
    </View>;

    const renderReview = (review: typeof REVIEWS[number]) => {
        const isLiked = liked.includes(review.id);

        return <Card
            variant="outline"
            key={review.id}
        >
            <Card.Content>
                <View
                    style={{
                        gap: spaces.spacingSm
                    }}
                >
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Avatar
                            title={t(`blocks.product01.review.${review.id}.name`)}
                            size="small"
                        />
                        <View
                            style={stylesheet.grow}
                        >
                            <Text
                                variant="labelMediumSize"
                                numberOfLines={1}
                                color="high"
                            >
                                {t(`blocks.product01.review.${review.id}.name`)}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="mid"
                            >
                                {t(`blocks.product01.review.${review.id}.date`)}
                            </Text>
                        </View>
                        {
                            isCompact ?
                                null
                            :
                                <Badge
                                    title={t("blocks.product01.reviews.verified")}
                                    spreadBehaviour="free"
                                    icon={CheckIcon}
                                    variant="tonal"
                                    type="success"
                                />
                        }
                    </View>
                    <View
                        accessibilityLabel={t("blocks.product01.ratingLabel", [
                            review.stars
                        ])}
                        accessibilityRole="image"
                    >
                        <Stars
                            empty={colors.content.border.default}
                            fill={categorical["4"]}
                            size={iconSizes.small}
                            value={review.stars}
                        />
                    </View>
                    <View
                        style={{
                            gap: spaces.spacingXxs
                        }}
                    >
                        <Text
                            variant="titleSmallSize"
                            color="high"
                        >
                            {t(`blocks.product01.review.${review.id}.title`)}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t(`blocks.product01.review.${review.id}.text`)}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.row}
                    >
                        <Button
                            title={t("blocks.product01.reviews.helpful", [
                                review.helpful + (isLiked ? 1 : 0)
                            ])}
                            variant={isLiked ? "tonal" : "ghost"}
                            onPress={handleLike(review.id)}
                            icon={LikeIcon}
                            type="neutral"
                            size="small"
                        />
                    </View>
                </View>
            </Card.Content>
        </Card>;
    };

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.product01.footer.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderContent = (children: ReactNode) => <View
        style={[
            stylesheet.wrapper,
            {
                maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg
            }
        ]}
    >
        {children}
    </View>;

    const renderSplit = (left: ReactNode, right: ReactNode) => <View
        style={[
            stylesheet.columns,
            {
                gap: isStacked ? spaces.spacingLg : spaces.spacingXxl,
                flexDirection: isStacked ? "column" : "row"
            }
        ]}
    >
        <View
            style={[
                stylesheet.grow,
                stylesheet.full
            ]}
        >
            {left}
        </View>
        <View
            style={{
                width: isStacked ? "100%" : shapeConfigs.sheetMaxWidth
            }}
        >
            {right}
        </View>
    </View>;

    return <ScrollView>
        <View>
            {
                renderContent(<View
                    style={[
                        stylesheet.row,
                        {
                            paddingVertical: spaces.spacingSm,
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Svg
                            height={iconSizes.large * 1.5}
                            width={iconSizes.large * 1.5}
                            viewBox="0 0 28 28"
                        >
                            <Circle
                                fill={ink}
                                cx={14}
                                cy={14}
                                r={14}
                            />
                            <Path
                                d={MARK_BAG}
                                fill={paper}
                            />
                            <Path
                                strokeLinecap="round"
                                strokeWidth={1.6}
                                d={MARK_HANDLE}
                                stroke={paper}
                                fill="none"
                            />
                        </Svg>
                        <Text
                            variant="titleLargeSize"
                            color="high"
                        >
                            {t("blocks.product01.brand")}
                        </Text>
                    </View>
                    {
                        isStacked ?
                            null
                        :
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingXxs
                                    }
                                ]}
                            >
                                {NAV.map(renderNav)}
                            </View>
                    }
                    <TextInput
                        accessibilityLabel={t("blocks.product01.search")}
                        placeholder={t("blocks.product01.search")}
                        style={stylesheet.search}
                        icon={SearchIcon}
                        size="small"
                    />
                    {
                        isCompact ?
                            null
                        :
                            <Button
                                accessibilityLabel={t("blocks.product01.account")}
                                onPress={handlePress}
                                variant="ghost"
                                icon={UserIcon}
                                type="neutral"
                                size="small"
                            />
                    }
                    <Badge.Indicator
                        count={cartCount}
                        type="primary"
                    >
                        <Button
                            accessibilityLabel={t("blocks.product01.cart")}
                            onPress={handlePress}
                            variant="ghost"
                            icon={CartIcon}
                            type="neutral"
                            size="small"
                        />
                    </Badge.Indicator>
                </View>)
            }
            <Separator
                length="100%"
            />
        </View>
        {
            renderContent(<View
                style={{
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }}
            >
                <Breadcrumb
                    accessibilityLabel={t("blocks.product01.breadcrumb")}
                    compactBehaviour="collapse"
                >
                    <Breadcrumb.List>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.product01.home")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.product01.nav.coffees")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Link
                                title={t("blocks.product01.filterCoffees")}
                                onPress={handlePress}
                            />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <Breadcrumb.Page
                                title={t("blocks.product01.name")}
                            />
                        </Breadcrumb.Item>
                    </Breadcrumb.List>
                </Breadcrumb>
                {
                    renderSplit(<View
                        style={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <View
                            style={[
                                stylesheet.stage,
                                {
                                    backgroundColor: getArea(SCENE_COLORS[scene] ?? "1"),
                                    borderRadius: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.full,
                                    {
                                        padding: spaces.spacingXxl
                                    }
                                ]}
                            >
                                <Scene
                                    fill={getColor(SCENE_COLORS[scene] ?? "1")}
                                    paper={paper}
                                    kind={scene}
                                    ink={ink}
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.announcement,
                                    {
                                        left: spaces.spacingMd,
                                        top: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Badge
                                    title={t("blocks.product01.badge.season")}
                                    variant="tonal"
                                    type="primary"
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.announcement,
                                    {
                                        bottom: spaces.spacingMd,
                                        right: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Badge
                                    title={`${SCENES.indexOf(scene) + 1} / ${SCENES.length}`}
                                    variant="outline"
                                    type="neutral"
                                />
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.thumbs,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {SCENES.map(renderThumb)}
                        </View>
                    </View>, <View
                        style={{
                            gap: spaces.spacingLg
                        }}
                    >
                        <View
                            style={{
                                gap: spaces.spacingSm
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                {BADGES.map(renderBadge)}
                            </View>
                            <Text
                                variant="headlineMediumSize"
                                accessibilityRole="header"
                                color="high"
                            >
                                {t("blocks.product01.name")}
                            </Text>
                            <Text
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {t("blocks.product01.lead")}
                            </Text>
                            <View
                                style={[
                                    stylesheet.row,
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <View
                                    accessibilityLabel={t("blocks.product01.ratingLabel", [
                                        decimal.format(4.9)
                                    ])}
                                    accessibilityRole="image"
                                >
                                    <Stars
                                        empty={colors.content.border.default}
                                        size={iconSizes.medium}
                                        fill={categorical["4"]}
                                        value={4.9}
                                    />
                                </View>
                                <Text
                                    variant="labelMediumSize"
                                    color="high"
                                >
                                    {decimal.format(4.9)}
                                </Text>
                                <Button
                                    title={t("blocks.product01.readReviews", [
                                        REVIEW_TOTAL
                                    ])}
                                    onPress={handlePress}
                                    variant="link"
                                />
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.priceRow,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Text
                                variant="headlineLargeSize"
                                color="high"
                            >
                                {money.format(unitPrice)}
                            </Text>
                            {
                                size === "250" ?
                                    <>
                                        <Text
                                            style={stylesheet.strike}
                                            variant="bodyLargeSize"
                                            color="mid"
                                        >
                                            {money.format(OLD_PRICE)}
                                        </Text>
                                        <Badge
                                            title={t("blocks.product01.discount")}
                                            variant="tonal"
                                            type="danger"
                                        />
                                    </>
                                :
                                    null
                            }
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingXs
                            }}
                        >
                            <Text
                                variant="labelMediumSize"
                                color="mid"
                            >
                                {t("blocks.product01.notes")}
                            </Text>
                            <View
                                style={[
                                    stylesheet.wrap,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                {NOTES.map(renderNote)}
                            </View>
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingXs
                            }}
                        >
                            <View
                                style={stylesheet.spread}
                            >
                                <Text
                                    variant="labelMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.product01.size")}
                                </Text>
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.product01.kiloPrice", [
                                        money.format(980)
                                    ])}
                                </Text>
                            </View>
                            <ToggleGroup
                                title={t("blocks.product01.size")}
                                attachBehaviour="attached"
                                onValueChange={handleSize}
                                isClearEnabled={false}
                                variant="outline"
                                value={size}
                            >
                                {SIZES.map(renderSize)}
                            </ToggleGroup>
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                stylesheet.columns,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={stylesheet.grow}
                            >
                                <SelectBox
                                    title={t("blocks.product01.grind")}
                                    titleExtractor={getOptionTitle}
                                    keyExtractor={getOptionKey}
                                    spreadBehaviour="stretch"
                                    defaultValue="filter"
                                    data={grindOptions}
                                />
                            </View>
                            <View
                                style={{
                                    width: shapeConfigs.fieldMinWidth
                                }}
                            >
                                <NumericInput
                                    title={t("blocks.product01.quantity")}
                                    onChangeText={setQuantity}
                                    spreadBehaviour="stretch"
                                    isShowStepper={true}
                                    value={quantity}
                                    max={10}
                                    min={1}
                                />
                            </View>
                        </View>
                        <View
                            style={{
                                gap: spaces.spacingSm
                            }}
                        >
                            <Button
                                title={t("blocks.product01.add", [
                                    money.format(unitPrice * count)
                                ])}
                                spreadBehaviour="stretch"
                                onPress={handleAdd}
                                variant="filled"
                                type="primary"
                                size="large"
                            />
                            <Button
                                title={t("blocks.product01.subscribe")}
                                spreadBehaviour="stretch"
                                onPress={handlePress}
                                icon={RefreshIcon}
                                variant="outline"
                                type="neutral"
                                size="large"
                            />
                        </View>
                        <Alert
                            description={t("blocks.product01.deliveryText", [
                                dateFormat.format(new Date(2026, 10, 5))
                            ])}
                            title={t("blocks.product01.delivery")}
                            type="info"
                        />
                    </View>)
                }
                {
                    renderSplit(<Accordion
                        defaultValue="info"
                        variant="plain"
                    >
                        {SECTIONS.map(renderSection)}
                    </Accordion>, <Card
                        variant="filled"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.product01.brew.title")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    {BREW.map(renderBrew)}
                                </View>
                                <Text
                                    variant="bodyMediumSize"
                                    color="mid"
                                >
                                    {t("blocks.product01.brew.text")}
                                </Text>
                            </View>
                        </Card.Content>
                    </Card>)
                }
                <View
                    style={{
                        gap: spaces.spacingMd
                    }}
                >
                    <View
                        style={[
                            stylesheet.spread,
                            stylesheet.wrap,
                            {
                                gap: spaces.spacingSm
                            }
                        ]}
                    >
                        <Text
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.product01.reviews.title")}
                        </Text>
                        <Button
                            title={t("blocks.product01.reviews.write")}
                            onPress={handlePress}
                            variant="outline"
                            type="neutral"
                            size="small"
                        />
                    </View>
                    <View
                        style={[
                            stylesheet.columns,
                            {
                                flexDirection: isStacked ? "column" : "row",
                                gap: spaces.spacingXl
                            }
                        ]}
                    >
                        <View
                            style={{
                                width: isStacked ? "100%" : shapeConfigs.cardWidth,
                                gap: spaces.spacingMd
                            }}
                        >
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        alignItems: "flex-end",
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="displayMediumSize"
                                    color="high"
                                >
                                    {decimal.format(4.9)}
                                </Text>
                                <View
                                    style={{
                                        paddingBottom: spaces.spacingXs,
                                        gap: spaces.spacingXxs
                                    }}
                                >
                                    <Stars
                                        empty={colors.content.border.default}
                                        fill={categorical["4"]}
                                        size={iconSizes.small}
                                        value={4.9}
                                    />
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.product01.reviews.basedOn", [
                                            REVIEW_TOTAL
                                        ])}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={{
                                    gap: spaces.spacingXs
                                }}
                            >
                                {HISTOGRAM.map(renderBar)}
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.grow,
                                stylesheet.full,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {REVIEWS.map(renderReview)}
                        </View>
                    </View>
                </View>
            </View>)
        }
        <View>
            <Separator
                length="100%"
            />
            {
                renderContent(<View
                    style={[
                        stylesheet.spread,
                        stylesheet.wrap,
                        {
                            paddingVertical: spaces.spacingLg,
                            gap: spaces.spacingSm
                        },
                        isCompact ?
                            {
                                alignItems: "flex-start",
                                flexDirection: "column"
                            }
                        :
                            null
                    ]}
                >
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t("blocks.product01.copyright")}
                    </Text>
                    <View
                        style={[
                            stylesheet.row,
                            stylesheet.wrap,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        {FOOTER.map(renderFooterLink)}
                    </View>
                </View>)
            }
        </View>
    </ScrollView>;
};
export default Product01;
